// Tiny fetch wrapper around the Worker.
//
// All requests use credentials: 'include' so the cross-origin session
// cookie (SameSite=None; Secure) rides along.
//
// Preview-mode (2026-05-23): when the SPA boots under /preview/* the
// `apiPathPrefix` is set to "/preview" and prepended to every
// outbound request path. The Worker mirrors every data route under
// /preview/<route> backed by `preview:`-prefixed KV. Live and preview
// are fully separate KV namespaces — operator visits /preview/ to
// review a non-production run without affecting client-facing data.

const WORKER_URL = __WORKER_URL__;
export const PROJECT_SLUG = __PROJECT_SLUG__;

// Detected once at module load. `/preview/...` → "/preview"; everything
// else → "". Read by `send()` below; exposed via `apiPathPrefix()` so
// callers (the router, anchor href construction) can preserve the
// prefix in client-rendered URLs.
const PATH_PREFIX = (() => {
  if (typeof window === "undefined") return "";
  const path = window.location.pathname || "/";
  if (path === "/preview" || path.startsWith("/preview/")) return "/preview";
  return "";
})();

export const apiPathPrefix = (): string => PATH_PREFIX;
export const inPreviewMode = (): boolean => PATH_PREFIX === "/preview";

// #94: one passkey identity spans N projects. The active project is chosen by
// the recipient (post-login selector / shell switcher, see app.ts) and sent as
// the X-Project-Slug header on every data request; the Worker's grant-check
// middleware verifies a grant for (email, project) and scopes the session. The
// choice persists in localStorage so a reload keeps the same project.
const ACTIVE_PROJECT_LS_KEY = "es_portal_active_project";

// Falls back to the build-time __PROJECT_SLUG__ so single-project instances
// (and the very first request before a selection is made) still send a header;
// on a multi-project instance app.ts resolves this against /my-projects and
// calls setActiveProject with a granted slug.
export function getActiveProject(): string | null {
  if (typeof localStorage !== "undefined") {
    const stored = localStorage.getItem(ACTIVE_PROJECT_LS_KEY);
    if (stored) return stored;
  }
  return PROJECT_SLUG || null;
}

export function setActiveProject(slug: string): void {
  if (typeof localStorage !== "undefined") {
    localStorage.setItem(ACTIVE_PROJECT_LS_KEY, slug);
  }
}

export function clearActiveProject(): void {
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem(ACTIVE_PROJECT_LS_KEY);
  }
}

export interface MyProjectsResponse {
  email: string;
  projects: Array<{ slug: string }>;
}

// Bootstrap: which projects may this identity access? Served OUTSIDE the
// grant-check chokepoint (chicken-and-egg), so it needs only a session, not an
// X-Project-Slug header.
export function fetchMyProjects(): Promise<MyProjectsResponse> {
  return getJson<MyProjectsResponse>("/my-projects");
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(`api ${status}`);
  }
}

async function send(
  path: string,
  init: RequestInit,
): Promise<{ status: number; body: unknown }> {
  // #94: attach the active project so the Worker can scope the email-only
  // session. Merged onto (not replacing) any caller headers, kept as a plain
  // object so it composes with the existing Content-Type headers.
  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string> | undefined),
  };
  const active = getActiveProject();
  if (active) headers["X-Project-Slug"] = active;
  const res = await fetch(`${WORKER_URL}${PATH_PREFIX}${path}`, {
    ...init,
    headers,
    credentials: "include",
  });
  let body: unknown = null;
  if (res.status !== 204) {
    try {
      body = await res.json();
    } catch {
      body = null;
    }
  }
  return { status: res.status, body };
}

export async function getJson<T>(path: string): Promise<T> {
  const { status, body } = await send(path, { method: "GET" });
  if (status < 200 || status >= 300) throw new ApiError(status, body);
  return body as T;
}

export async function postJson<T>(path: string, payload: unknown): Promise<T> {
  const { status, body } = await send(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (status < 200 || status >= 300) throw new ApiError(status, body);
  return body as T;
}

export async function putJson<T>(path: string, payload: unknown): Promise<T> {
  const { status, body } = await send(path, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (status < 200 || status >= 300) throw new ApiError(status, body);
  return body as T;
}

export async function postEmpty(path: string, payload: unknown): Promise<void> {
  const { status, body } = await send(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (status < 200 || status >= 300) throw new ApiError(status, body);
}

export async function deleteJson<T>(path: string): Promise<T> {
  const { status, body } = await send(path, { method: "DELETE" });
  if (status < 200 || status >= 300) throw new ApiError(status, body);
  return body as T;
}

// Download a Worker file route (an .xlsx / .docx attachment) via the
// authenticated fetch layer, then hand the bytes to the browser as a blob.
//
// Why NOT a bare `<a href download>`: post-#94 the data router's grant-check
// middleware (worker/src/routes/data.ts) REQUIRES the `X-Project-Slug` header
// on every route and returns `400 {"error":"missing-project"}` without it. A
// bare-anchor navigation/download cannot attach custom headers, so it hits the
// 400 — Firefox renders that JSON inline ("a coding screen") and Chrome silently
// downloads the error JSON as a corrupt file. (Pre-#94 the session cookie
// carried the project, so anchors worked — hence "it wasn't there in testing".)
//
// Fetching through `send()`'s header/credential path fixes the auth; a `blob:`
// object URL always honors `download` cross-browser, so the tab can never
// navigate to an error page. Throws ApiError on a non-2xx response so callers
// surface an inline error instead of a broken download.
export async function downloadFile(path: string, filename?: string): Promise<void> {
  const headers: Record<string, string> = {};
  const active = getActiveProject();
  if (active) headers["X-Project-Slug"] = active;
  const res = await fetch(`${WORKER_URL}${PATH_PREFIX}${path}`, {
    method: "GET",
    headers,
    credentials: "include",
  });
  if (res.status < 200 || res.status >= 300) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      body = null;
    }
    throw new ApiError(res.status, body);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = objectUrl;
    // For a blob: URL the server's Content-Disposition is ignored — the
    // `download` value is the saved filename, so pass it explicitly.
    a.download = filename ?? "";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Revoke after the click's download has been kicked off. Revoking
    // synchronously can cancel the in-flight download in some browsers.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  }
}

// Wire a static download control (an <a role="button"> icon) to downloadFile.
// Handles BOTH click and Space/Enter (a role="button" must respond to Space;
// an anchor fires a synthetic click on Enter but not Space), calls
// e.preventDefault() so the "#" href never navigates, and routes a failure to a
// caller-supplied VISIBLE error sink (a tooltip alone is invisible to a user who
// clicked and isn't hovering). A 401 gets a session-expired message.
export function wireDownloadControl(
  control: HTMLElement,
  path: string,
  filename: string,
  showError: (message: string) => void,
): void {
  const run = (): void => {
    void downloadFile(path, filename).catch((err) => {
      showError(
        err instanceof ApiError && err.status === 401
          ? "Session expired — reload the page to sign in, then try again."
          : `Download failed: ${err instanceof Error ? err.message : String(err)}`,
      );
    });
  };
  control.addEventListener("click", (e) => {
    e.preventDefault();
    run();
  });
  control.addEventListener("keydown", (e) => {
    if (e.key === " ") {
      e.preventDefault();
      run();
    }
  });
}
